import React, { useState, useEffect, useRef } from 'react';
import CustomScrollTrigger from 'js/wrapper/CustomScrollTrigger';
import { Parallax } from 'react-scroll-parallax';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

import LoginForm from 'components/forms/auth/LoginForm';
import RichEditorContent from 'components/RichEditorContent';
import { useAbstractCmsElement } from 'components/cms/hooks/useAbstractCmsElementNew';


const CmsElementLoginForm = (props) => {
  const {
    editMode,
    index,
    showElementSettings,
    intl
  } = props;

  // CMS Logic via Hook
  const {
    shown,
    setShown,
    getMainId,
    getCustomId,
    getParamValue,
    getBackgroundStyle,
    elementStyleTag,
    getParallaxData,
    getAppendHtml,
    cssClasses,
    responsiveClasses
  } = useAbstractCmsElement(props);

  // dnd-kit Sortable Hook
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging
  } = useSortable({
    id: index,
    disabled: !editMode
  });

  // Local State
  const [pending, setPending] = useState(false);
  const timeoutRef = useRef(null);
  const messages = intl?.messages || {};

  // Cleanup Timeout on Unmount
  useEffect(() => {
    return () => {
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
    };
  }, []);

  const dndStyle = {
    transform: CSS.Translate.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
    zIndex: isDragging ? 1000 : 'auto',
    position: 'relative',
    cursor: editMode ? 'move' : 'default',
    width: '100%'
  };

  const doNothing = (e) => {
    e.stopPropagation();
    e.preventDefault();
  };

  const getSocialMediaButtons = () => {
    return (
      <>
        <a className="btn btn-social btn-facebook m-l-10 m-t-10" href="#facebook" onClick={doNothing} key="f">
          <i className="fab fa-facebook-f m-r-5" /> Facebook
        </a>
        <a className="btn btn-social btn-google m-l-10 m-t-10" href="#google" onClick={doNothing} key="g">
          <i className="fab fa-google m-r-5" /> Google
        </a>
        <a className="btn btn-social btn-microsoft m-l-10 m-t-10" href="#microsoft" onClick={doNothing} key="m">
          <i className="fab fa-microsoft m-r-5" /> Microsoft
        </a>
      </>
    );
  };

  // Logic & Classes
  const isVisible = editMode || shown;
  const shownClass = isVisible ? 'elem-shown' : 'elem-hidden';
  const textClass = `text-${getParamValue('mainColor', 'master')}`;
  const showSocialMediaLogin = true;

  const innerContent = (
    <div
      id={getMainId()}
      className={`ms-login-form-content ${shownClass} ${cssClasses} ${responsiveClasses}`.trim()}
      onClick={(e) => editMode && showElementSettings(e, index)}
      style={getBackgroundStyle()}
    >
      {elementStyleTag}

      <div className="ms-login-form-container">
        <h4 className={`no-margin p-b-5 ${textClass}`}>
          <i className="fa fa-lock m-r-5" />
          {getParamValue('title', '')}
        </h4>


        <RichEditorContent
          content={`<p class="${textClass}">${getParamValue('content', '')}</p>`}
        />


        <LoginForm
          onSubmit={(e) => e?.preventDefault()}
          buttonTitle={getParamValue('buttonTitle', messages.button_signin || 'Sign In')}
        />

        {showSocialMediaLogin && (
          <div className={`m-t-30 ${textClass}`}>
            <h4 className={textClass}>
              <center>{messages.alternative_login_long}</center>
            </h4>
            <center>{getSocialMediaButtons()}</center>
          </div>
        )}
      </div>

      {getAppendHtml()}
    </div>
  );

  const parallaxData = getParallaxData();

  return (
    <div
      ref={setNodeRef}
      style={dndStyle}
      {...attributes}
      {...listeners}
    >
      <div>
        <CustomScrollTrigger className="cms-overflow-fix" onEnter={() => setShown(true)} {...getCustomId()}>
          {parallaxData ? (
            <Parallax {...parallaxData}>
              {innerContent}
            </Parallax>
          ) : (
            innerContent
          )}
        </CustomScrollTrigger>
      </div>
    </div>
  );
};

export default CmsElementLoginForm;
